// List results belong to the mounted overview. Feathers still supplies models for
// editing, but reading a list must not merge every record into the shared store.
export const findServiceRecords = (service, params = {}) => service.find({ ...params, skipStore: true })

const resolve = (value) => (typeof value === 'function' ? value() : unref(value))

export function useServiceList(service, params = {}, options = {}) {
  const state = shallowReactive({ data: [], total: 0, isPending: false, error: null, latestQuery: null, request: null })
  const condition = shallowRef(() => true)
  const active = shallowRef(true)
  const pagination = options.pagination || {}
  const requestParams = computed(() => {
    const current = resolve(params)
    if (current === null) return null
    const query = { ...current?.query }
    if (pagination.limit !== undefined) query.$limit = resolve(pagination.limit)
    if (pagination.skip !== undefined) query.$skip = resolve(pagination.skip)
    return { ...current, query }
  })
  const enabled = computed(() => active.value && requestParams.value !== null && resolve(options.enabled ?? true) && condition.value())
  let disposed = false
  let version = 0
  let eventVersion = 0
  let pending = null
  let scheduled = false
  let previousFilter = null

  const clear = () => {
    version += 1
    pending = null
    state.data = []
    state.total = 0
    state.error = null
    state.latestQuery = null
    state.request = null
    state.isPending = false
  }

  const find = (overrides) => {
    if (disposed || !enabled.value) return Promise.resolve({ data: [], total: 0 })
    const current = requestParams.value
    const request = { ...current, ...overrides, query: { ...current.query, ...overrides?.query } }
    const key = JSON.stringify(request)
    if (pending?.key === key && pending.eventVersion === eventVersion) return pending.promise
    const generation = ++version
    const sourceKey = JSON.stringify(current)
    state.isPending = true
    state.error = null
    const isCurrent = () => !disposed && enabled.value && generation === version && sourceKey === JSON.stringify(requestParams.value)

    const promise = (async () => {
      try {
        const response = await findServiceRecords(service, request)
        if (isCurrent()) {
          state.data = Array.isArray(response) ? response : response.data || []
          state.total = response.total ?? state.data.length
          state.latestQuery = { query: request.query, queriedAt: new Date().toISOString() }
        }
        return response
      } catch (error) {
        if (isCurrent()) state.error = error
        throw error
      } finally {
        if (generation === version) {
          state.isPending = false
          state.request = null
          pending = null
        }
      }
    })()
    pending = { key, eventVersion, promise }
    state.request = promise
    return promise
  }

  // Batch synchronous filter changes and service events into one request.
  // Automatic failures are exposed through state.error; manual callers can catch them.
  const schedule = () => {
    if (scheduled || disposed) return
    scheduled = true
    queueMicrotask(() => {
      scheduled = false
      if (!disposed) void find().catch(() => {})
    })
  }

  watch(
    () => [JSON.stringify(requestParams.value), enabled.value],
    ([, allowed]) => {
      if (!allowed) {
        clear()
        return
      }
      const { $skip, $limit, ...filter } = requestParams.value.query
      const filterKey = JSON.stringify({ filter, limit: $limit })
      if (pagination.page && previousFilter !== null && previousFilter !== filterKey) {
        pagination.skip.value = 0
        pagination.page.value = 1
      }
      previousFilter = filterKey
      schedule()
    },
    { immediate: options.immediate !== false },
  )

  const onMutation = () => {
    eventVersion += 1
    schedule()
  }
  const events = ['created', 'updated', 'patched', 'removed']
  let subscribed = false
  const subscribe = () => {
    if (subscribed || options.realtime === false) return
    events.forEach((event) => service.on?.(event, onMutation))
    subscribed = true
  }
  const unsubscribe = () => {
    if (!subscribed) return
    events.forEach((event) => service.removeListener?.(event, onMutation))
    subscribed = false
  }
  subscribe()
  if (getCurrentInstance()) {
    onDeactivated(() => {
      active.value = false
      clear()
      unsubscribe()
    })
    onActivated(() => {
      active.value = true
      subscribe()
    })
  }
  onScopeDispose(() => {
    disposed = true
    version += 1
    unsubscribe()
  })

  return Object.assign(state, { find, queryWhen: (predicate) => (condition.value = predicate) })
}
